/**
 * platformSpecs — the typed, canonical canvas size + JPEG quality per platform.
 *
 * These are the sizes the design doc width/height SHOULD match (the editor sizes
 * its stage to the spec, and buildDefaultDesign uses the spec dimensions). The
 * server render emits at spec.width x spec.height (scaled by dpr) so the
 * published image is always the platform-correct aspect ratio.
 *
 * PURE CJS, no node-only requires — importable from both ends.
 *
 * @module render/platformSpecs
 */
const PLATFORM_SPECS = {
    // 4:5 portrait — the highest-reach Instagram feed ratio.
    instagram: { width: 1080, height: 1350, quality: 0.9 },
    // ~1.91:1 landscape link-card ratio.
    facebook: { width: 1200, height: 630, quality: 0.9 },
    // A Facebook Community (Group) post uses the same canvas as a Page post.
    facebook_group: { width: 1200, height: 630, quality: 0.9 },
    // LinkedIn's recommended single-image landscape ratio.
    linkedin: { width: 1200, height: 627, quality: 0.9 },
};

// The named canvas sizes a PRESET can adopt. Presets replaced the platform concept:
// a preset owns its own canvas, and these are the reusable starting sizes the editor
// offers (plus a free custom W×H). Each carries a short aspect label for the chip the
// node + the preset rail show. ONE source for the size dropdown and the seed presets.
const CANVAS_PRESETS = [
    { id: 'portrait', label: 'Hochformat', aspect: '4:5', width: 1080, height: 1350 },
    { id: 'square', label: 'Quadrat', aspect: '1:1', width: 1080, height: 1080 },
    { id: 'landscape', label: 'Querformat', aspect: '1.91:1', width: 1200, height: 630 },
    { id: 'story', label: 'Story', aspect: '9:16', width: 1080, height: 1920 },
];

// The closest named aspect label for arbitrary dimensions (for the node/rail chip on
// a custom-sized preset). Falls back to the reduced ratio when nothing matches.
const gcd = (a, b) => (b ? gcd(b, a % b) : a);
const aspectLabel = (width, height) => {
    if (!width || !height) return '';
    const match = CANVAS_PRESETS.find(c => c.width * height === c.height * width);
    if (match) return match.aspect;
    const d = gcd(width, height) || 1;
    return `${Math.round(width / d)}:${Math.round(height / d)}`;
};

export { PLATFORM_SPECS, CANVAS_PRESETS, aspectLabel };
